import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '../../../../lib/dataSource'
import type { RecurrenceRule } from '../../../../lib/db/models'
import { recurrenceUpdate } from '../../../../lib/recurrence'
import { TaskEntry, TitleRecognitionScope, type TaskCreationData } from '../../components/TaskEntry'
import { toCreateTask } from '../../utils/toCreateTask'
import { orderAtEndOf } from '../../utils/appendTaskOrders'
import { showCreatedElsewhereToast } from '../../utils/createdElsewhereToast'
import { resolveTagNames } from '../../utils/resolveTagNames'
import { useTags } from '../../hooks/useTags'
import { TaskDropdown } from '../../components/TaskDropdown'
import { RecurrenceDropdownContent } from '../../components/RecurrenceDropdown'

interface RecurringCreatorProps {
  lifeSpace: 'personal' | 'work' | 'home'
  /** Die Aufgabe steht in der DB — die Seite schließt den Anleger und wählt die neue Zeile. */
  onCreated: (taskId: string) => void
  /** Esc im Titel (oder leer verlassen) — es ist nichts angelegt. */
  onCancel: () => void
}

/**
 * **Anlegen auf Wiederkehrend** (CONTEXT.md, Wiederkehrend; entschieden 2026-09-13):
 * **erst der Titel, dann der Rhythmus — gespeichert erst nach beidem.**
 *
 * Die normale Anlege-Zeile nimmt den Titel. Ihr Enter speichert hier NICHT, sondern hält die
 * Eingabe fest und öffnet den **Wiederkehr-Wähler** an der Zeile. Erst **Bestätigen** legt die
 * Aufgabe an, ohne Projekt, im Lebensbereich — mit festem Rhythmus samt erstem Termin. Esc im
 * Wähler führt zum Titel zurück, der Text bleibt (die Zeile bleibt montiert und behält ihr
 * Formular). So liegt nie eine Aufgabe ohne Titel oder eine ohne Rhythmus in der DB.
 */
export const RecurringCreator: React.FC<RecurringCreatorProps> = ({ lifeSpace, onCreated, onCancel }) => {
  const [pending, setPending] = useState<TaskCreationData | null>(null)
  const source = useDataSource()
  const { tags: allTags, createTag } = useTags()
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const pickerRef = useRef<HTMLDivElement>(null)

  // **Der Fokus geht in den Wähler** (gefunden im E2E, 2026-09-13): Blieb er im Titel, landete
  // Esc dort und brach den ganzen Anleger ab; Enter bestätigte nicht. Auf den gewählten Chip,
  // damit Enter/Esc beim Wähler ankommen und Tab durch seine Knöpfe läuft.
  // Das Portal steht nicht zwingend im ersten Bild — ein paar Bilder nachfassen.
  const isOpen = pending != null
  useEffect(() => {
    if (!isOpen) return
    let rafId = 0
    let tries = 0
    const focusPicker = () => {
      const root = pickerRef.current
      const target = root?.querySelector<HTMLElement>('.recurrence-dropdown__chip--selected') ?? root?.querySelector<HTMLElement>('button')
      if (target) target.focus()
      else if (tries++ < 10) rafId = requestAnimationFrame(focusPicker)
    }
    rafId = requestAnimationFrame(focusPicker)
    return () => cancelAnimationFrame(rafId)
  }, [isOpen])

  const backToTitle = useCallback(() => {
    setPending(null)
    requestAnimationFrame(() => rootRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus())
  }, [])

  const create = useCallback(async (rule: RecurrenceRule, data: TaskCreationData | null = pending) => {
    if (!data) return
    const id = crypto.randomUUID()
    // `#Projekt` im Titel (#234): dorthin, ans Ende — sonst ohne Projekt im Bereich der Seite.
    const { target } = data
    const place = target
      ? { lifeSpace: target.lifeSpace, projectId: target.projectId, sectionId: target.sectionId, order: await orderAtEndOf(source, target.projectId, target.sectionId) }
      : { lifeSpace, projectId: null, sectionId: null }
    // Neue Tags aus dem Titel entstehen erst hier — brach man den Wähler ab, gäbe es sie sonst leer (#241).
    const tagIds = await resolveTagNames(data.newTagNames ?? [], allTags, createTag)
    const withTags = tagIds.length ? { ...data, tags: [...new Set([...(data.tags ?? []), ...tagIds])] } : data
    const update = recurrenceUpdate(rule, new Date())
    const created = await source.createTask({
      ...toCreateTask(withTags, { id, ...place }),
      // Zuletzt: Die Regel setzt das Wann und schlägt damit ein vorher gewähltes.
      ...update,
      // Nur den Tag — eine Uhrzeit aus dem Titel („jeden Montag 9 Uhr“) bleibt (Code-Review 03.10.2026).
      ...(update.startDate && data.startFromTitle ? { startTime: data.startTime ?? null, startPeriod: data.startPeriod ?? null } : {}),
    })
    setPending(null)
    if (target && target.lifeSpace !== lifeSpace) {
      onCancel()
      showCreatedElsewhereToast(t, target.projectName, created)
    } else {
      onCreated(id)
    }
  }, [pending, lifeSpace, onCreated, onCancel, source, t, allTags, createTag])

  return (
    <div className="recurring-page__creator" ref={rootRef}>
      <TitleRecognitionScope lifeSpace={lifeSpace}>
        <TaskEntry
          mode="creating"
          deferNewTags
          // Steht die Regel schon fest (im Titel: „jeden Montag“, oder am Chip), gibt es nichts zu wählen —
          // der Wähler böte ohne Änderung kein „Bestätigen“, und Enter schlösse ihn nur.
          onSubmitCreate={(data) => { if (data.recurrence) void create(data.recurrence, data); else setPending(data) }}
          // Esc im Titel verwirft alles — außer der Wähler ist noch offen: Dann schließt es nur ihn.
          onCancel={() => { if (pending) backToTitle(); else onCancel() }}
          autoFocus
        />
      </TitleRecognitionScope>
      <TaskDropdown
        open={pending != null}
        onOpenChange={(open) => { if (!open) backToTitle() }}
        side="bottom"
        align="start"
        trigger={<span className="recurring-page__creator-anchor" aria-hidden="true" />}
      >
        <div ref={pickerRef}>
          <RecurrenceDropdownContent
            // Mit Regel geht Enter gar nicht hierher (siehe oben) — der Wähler beginnt leer.
            value={null}
            seedDate={pending?.startDate ?? null}
            onValueChange={(rule) => { void create(rule) }}
            onClose={backToTitle}
            placement="actionbar"
          />
        </div>
      </TaskDropdown>
    </div>
  )
}
